/**
 * Staff Login Page - Full page login for staff/admin users
 * Includes app download links and certificates section
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { TwoFactorVerifyLogin } from '@/components/auth/TwoFactorVerifyLogin';
import { ReConsentModal } from '@/components/auth/ReConsentModal';
import { OAuthProviderButtons } from '@/components/auth/OAuthProviderButtons';
import { config } from '@/src/config';
import { tvTokens } from '@/src/platform/shared/components/triveny';
import { AUTH_BACKDROP } from '@/src/platform/shared/components/authBackdrop';
import {
  DESKTOP_MACOS_PUBLISHED,
  DESKTOP_WINDOWS_PUBLISHED,
  usePublishedDesktopVersion,
  macDownloadUrl,
  windowsDownloadUrl,
  MOBILE_IOS_PUBLISHED,
  MOBILE_ANDROID_PUBLISHED,
  IOS_APP_URL,
  ANDROID_APP_URL,
} from '@/lib/desktopDownloads';
import {
  AlertCircle,
  Lock,
  Mail,
  LogIn,
  Monitor,
  Apple,
  Smartphone,
  Shield,
  CheckCircle,
  Clock,
  Download
} from 'lucide-react';

interface ConsentStatus {
  requires_consent: boolean;
  terms_needs_update: boolean;
  hipaa_needs_update: boolean;
  terms_current_version?: string;
  hipaa_current_version?: string;
}

interface TwoFactorChallengeResponse {
  requires_2fa: boolean;
  phone_number: string;
  phone_number_masked: string;
  message: string;
  email: string;
  consent_status?: ConsentStatus;
  available_methods?: string[];
  preferred_method?: string;
}

interface LoginSuccessResponse {
  access_token: string;
  refresh_token?: string;
  expires_in?: number;
  token_type: string;
  user: any;
  consent_status?: ConsentStatus;
}

// Helper function to complete login without AuthContext.
// Stores token and user in localStorage and redirects.
//
// Triveny enforces 2FA, so before sending the user into the app we ask the
// backend (GET /api/platform/auth/2fa/methods) for the LIVE enrollment
// state instead of trusting whatever the login-response user object says
// (the field can be stale in various edge cases: OAuth callbacks, race
// conditions, prior bugs that mis-cached false). This makes /setup-2fa
// the destination ONLY when the backend reports mfa_enabled=false for the
// real, freshly-issued JWT.
// Return a safe post-login destination from the `?next=` query param, or '/'.
// Only same-origin ABSOLUTE PATHS are allowed (must start with a single '/',
// not '//' or a scheme) so this can't be abused as an open redirect.
function getSafeNextPath(): string {
  try {
    const raw = new URLSearchParams(window.location.search).get('next');
    if (raw && raw.startsWith('/') && !raw.startsWith('//')) {
      return raw;
    }
  } catch {
    /* ignore malformed query */
  }
  return '/';
}

/**
 * Write the whole credential set a session needs to survive.
 *
 * This page bypasses AuthContext.completeLogin (it hard-navigates instead of
 * swapping React state), so it has to persist the same things by hand — and it
 * used to persist only the access token, throwing away the refresh token and
 * expiry the backend returns on every login (TRUS-537).
 *
 * The refresh token is the ONLY thing that lets a session survive a 401:
 * apiClient's 401 handler gates its refresh attempt on `refresh_token` being
 * present, so with none stored it declared the session unrecoverable on the
 * FIRST 401 — wiping auth state and POSTing /api/auth/logout, which blacklists
 * the access token AND revokes every refresh-token family the user has.
 * AuthContext's proactive-refresh timer degraded the same way: with nothing to
 * send, refreshAccessToken() returns false and its failure branch calls
 * logout(). Production bore it out — one super_admin accumulated 187 refresh
 * tokens, not one of them ever rotated, and was bounced back to /login within
 * seconds of each sign-in.
 *
 * Exported for the regression test; callers use completeLoginAndRedirect.
 */
export function persistLoginSession(
  token: string,
  user: any,
  refreshToken?: string,
  expiresIn?: number,
) {
  localStorage.setItem('auth_token', token);
  if (refreshToken) {
    localStorage.setItem('refresh_token', refreshToken);
  }
  if (expiresIn) {
    localStorage.setItem('token_expires_at', (Date.now() + expiresIn * 1000).toString());
  }
  localStorage.setItem('session_start', Date.now().toString());
  if (user) {
    try {
      localStorage.setItem('user', JSON.stringify(user));
    } catch {
      /* ignore quota / serialization errors */
    }
  }
  // Clear the persisted activeTab so a fresh login lands on the product's
  // default landing surface, not on whatever tab the user happened to be on
  // when the prior session ended. For Triveny that's the new full-bleed
  // triveny-home; for WeaveIn it falls back to the homepage.
  localStorage.removeItem('activeTab');
}

async function completeLoginAndRedirect(
  token: string,
  user: any,
  refreshToken?: string,
  expiresIn?: number,
) {
  persistLoginSession(token, user, refreshToken, expiresIn);

  // Honor a safe ?next= redirect (e.g. /accept-org-invite?token=… routes an
  // existing invited user back to finish accepting after a full login). Falls
  // back to the product home. Applies to both WeaveIn and Triveny.
  const dest = getSafeNextPath();

  if (!config.isTriveny) {
    // WeaveIn keeps the original redirect (home unless a next path was given).
    window.location.href = dest;
    return;
  }

  // Triveny: 2FA is OPTIONAL (self-serve) — never force enrollment. Users who
  // have enabled 2FA are still challenged earlier in the login flow
  // (requires_2fa); everyone else goes straight to the app. 2FA can be turned
  // on anytime in Settings.
  window.location.href = dest;
}

export default function StaffLoginPage() {
  const navigate = useNavigate();
  // Live published desktop version (from latest.json); null until deployed + CI-passed.
  const desktopVersion = usePublishedDesktopVersion();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // 2FA state
  const [show2FA, setShow2FA] = useState(false);
  const [twoFactorChallenge, setTwoFactorChallenge] = useState<TwoFactorChallengeResponse | null>(null);

  // Consent state
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [consentStatus, setConsentStatus] = useState<ConsentStatus | null>(null);
  const [pendingLoginData, setPendingLoginData] = useState<LoginSuccessResponse | null>(null);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const deviceToken = localStorage.getItem('device_token');

      // Product-aware login routing: Triveny.ai users authenticate against
      // platform_shared.tenant_users via the M&S /api/platform/auth/login
      // endpoint (returns a platform JWT with token_type="platform"). WeaveIn
      // users continue to hit /api/auth/login as before. The endpoints share
      // the same {email, password} request shape; only the response differs
      // (platform response has no 2FA / consent_status flow).
      const loginEndpoint = config.isTriveny
        ? '/api/platform/auth/login'
        : '/api/auth/login';

      const response = await fetch(`${config.weaveinApi.baseUrl}${loginEndpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(deviceToken && { 'X-Device-Token': deviceToken }),
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Login failed');
      }

      // Both products now support the 2FA challenge flow.
      // - Triveny: /api/platform/auth/login returns {requires_2fa: true,
      //   email, tenant_slug, available_methods: ['totp','backup'],
      //   preferred_method} when the user has TOTP enabled. The 2FA verify
      //   endpoints are /api/platform/auth/2fa/{verify-login, backup-codes/verify}.
      // - WeaveIn: same shape but with SMS support and the legacy /api/auth
      //   verify endpoints. Both flows feed the same TwoFactorVerifyLogin
      //   component, which routes by config.isTriveny.
      if (data.requires_2fa) {
        setTwoFactorChallenge(data);
        setShow2FA(true);
      } else {
        // No 2FA — issue the token immediately.
        // Triveny path skips the consent modal (handled in handleLoginSuccess).
        handleLoginSuccess(data);
      }
    } catch (err: any) {
      setError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleLoginSuccess = (loginData: LoginSuccessResponse) => {
    // Triveny is not a HIPAA-covered entity — skip the BAA/HIPAA re-consent
    // modal entirely. The backend response shouldn't ship a consent_status
    // for platform tokens, but defensive-gate here so a future regression
    // doesn't surface healthcare consent docs to a Triveny tenant user.
    if (!config.isTriveny && loginData.consent_status && loginData.consent_status.requires_consent) {
      setPendingLoginData(loginData);
      setConsentStatus(loginData.consent_status);
      setShowConsentModal(true);
    } else {
      completeLoginAndRedirect(
        loginData.access_token,
        loginData.user,
        loginData.refresh_token,
        loginData.expires_in,
      );
    }
  };

  const handleConsentComplete = () => {
    setShowConsentModal(false);
    if (pendingLoginData) {
      completeLoginAndRedirect(
        pendingLoginData.access_token,
        pendingLoginData.user,
        pendingLoginData.refresh_token,
        pendingLoginData.expires_in,
      );
    }
  };

  const handle2FASuccess = (
    token: string,
    user: any,
    consentStatusFromVerify?: ConsentStatus,
    refreshToken?: string,
    expiresIn?: number,
  ) => {
    const loginData: LoginSuccessResponse = {
      access_token: token,
      refresh_token: refreshToken,
      expires_in: expiresIn,
      token_type: 'bearer',
      user: user,
      consent_status: consentStatusFromVerify
    };
    handleLoginSuccess(loginData);
  };

  const handle2FACancel = () => {
    setShow2FA(false);
    setTwoFactorChallenge(null);
    setEmail('');
    setPassword('');
  };

  const handleResendCode = async () => {
    if (!twoFactorChallenge) return;
    const deviceToken = localStorage.getItem('device_token');

    const response = await fetch(`${config.weaveinApi.baseUrl}/api/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(deviceToken && { 'X-Device-Token': deviceToken }),
      },
      body: JSON.stringify({ email, password }),
    });

    const data = await response.json();
    if (data.requires_2fa) {
      setTwoFactorChallenge(data);
    }
  };

  // Show consent modal
  if (showConsentModal && consentStatus && pendingLoginData) {
    return (
      <ReConsentModal
        consentStatus={consentStatus}
        onComplete={handleConsentComplete}
        userEmail={email}
        accessToken={pendingLoginData.access_token}
      />
    );
  }

  // Show 2FA verification
  if (show2FA && twoFactorChallenge) {
    const wrapperClass = config.isTriveny
      ? 'triveny-platform'
      : AUTH_BACKDROP;
    const wrapperStyle: React.CSSProperties | undefined = config.isTriveny
      ? { minHeight: '100vh', background: tvTokens.paper, fontFamily: tvTokens.fontSans }
      : undefined;
    return (
      <div className={wrapperClass} style={wrapperStyle}>
        <div className="max-w-md mx-auto pt-20 px-4">
          <TwoFactorVerifyLogin
            email={twoFactorChallenge.email}
            phoneNumberMasked={twoFactorChallenge.phone_number_masked}
            onSuccess={handle2FASuccess}
            onCancel={handle2FACancel}
            onResendCode={handleResendCode}
            availableMethods={twoFactorChallenge.available_methods as ('sms' | 'totp' | 'backup')[] | undefined}
            preferredMethod={twoFactorChallenge.preferred_method as ('sms' | 'totp' | 'backup') | undefined}
          />
        </div>
      </div>
    );
  }

  // Shared login layout — same two-column structure for WeaveIn and Triveny.
  // Triveny renders the same chrome but with the Triveny design tokens:
  //   - .triveny-platform wrapper class enables the CSS overlay (paper bg,
  //     mono labels, ink buttons, hairline borders).
  //   - WeaveIn teal accents → ink + accent tokens.
  //   - /weavin-logo.png → Triveny wordmark.
  //   - "Member Login" header link is hidden (Triveny has no member portal).
  //   - "Get the WeaveIn App" right column → Triveny welcome content.
  // ───────────────────────────────────────────────────────────────────────
  // Primary accent: Triveny uses ink (--v2-ink) for CTAs to match the rest
  // of the platform; WeaveIn uses the teal accent. These two helpers return the
  // right inline-style block per surface so we don't have to interpolate
  // arbitrary Tailwind values.
  // Inline background so the brand color wins over the shared <Button> primary
  // variant's bg-[var(--primary)] — in dark mode --primary is near-white, which
  // otherwise leaves the base button colorless until hover. The variant's
  // hover:brightness-90 still provides the hover feedback.
  const primaryBtnStyle: React.CSSProperties = config.isTriveny
    ? { backgroundColor: tvTokens.ink, color: '#fff' }
    : { backgroundColor: 'var(--v2-accent)', color: '#fff' };
  const accentTextStyle: React.CSSProperties | undefined = config.isTriveny
    ? { color: tvTokens.accentInk }
    : undefined;
  const accentBgStyle: React.CSSProperties | undefined = config.isTriveny
    ? { backgroundColor: tvTokens.ink }
    : undefined;
  const rootClass = config.isTriveny
    ? 'triveny-platform min-h-screen'
    : AUTH_BACKDROP;
  const rootStyle: React.CSSProperties | undefined = config.isTriveny
    ? { background: tvTokens.paper, fontFamily: tvTokens.fontSans }
    : undefined;
  return (
    <div className={rootClass} style={rootStyle}>
      {/* Header */}
      <header className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            {/* Triveny: the wordmark links OUT to the public marketing site at
                the apex (triveny.ai) so users can get back to the public home
                from the login screen. (The app lives on app.triveny.ai.) Use a
                plain <a> for the cross-host navigation, not react-router Link.
                WeaveIn keeps its internal Link to "/". */}
            {config.isTriveny ? (
              <a
                href={
                  'https://' +
                  ((typeof window !== 'undefined' ? window.location.hostname : '')
                    .replace(/^(app|dev)\./, '') || 'triveny.ai')
                }
                className="flex items-center"
                aria-label="Triveny home"
              >
                <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 8 }}>
                  <span
                    style={{
                      fontFamily: tvTokens.fontDisplay,
                      fontSize: 22,
                      fontWeight: 600,
                      letterSpacing: '-0.02em',
                      color: tvTokens.ink,
                    }}
                  >
                    Triveny
                  </span>
                  <span
                    style={{
                      fontFamily: tvTokens.fontMono,
                      fontSize: 10,
                      color: tvTokens.ink400,
                      textTransform: 'uppercase',
                      letterSpacing: '0.14em',
                    }}
                  >
                    Marketing &amp; Sales
                  </span>
                </span>
              </a>
            ) : (
              <Link to="/" className="flex items-center">
                <img src="/weavein-wordmark.png" alt="WeaveIn" className="h-8 lg:h-10 xl:h-11 w-auto shrink-0" />
              </Link>
            )}
            <div className="flex items-center space-x-4">
              {!config.isTriveny && (
                <Link to="/member/login" className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100">
                  Member Login
                </Link>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-3 sm:px-4 py-6 sm:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-12 items-start">
          {/* Login Form */}
          <div>
            <Card className="shadow-lg">
              <CardHeader className="space-y-1 pb-4">
                <div className="flex items-center justify-center mb-4">
                  <div
                    className="w-14 h-14 rounded-full bg-v2accent flex items-center justify-center"
                    style={accentBgStyle}
                  >
                    <Lock className="w-7 h-7 text-white" />
                  </div>
                </div>
                <CardTitle className="text-xl sm:text-2xl font-bold text-center">
                  {config.isTriveny ? 'Triveny Login' : 'Staff Portal Login / Signup'}
                </CardTitle>
                <CardDescription className="text-center">
                  {config.isTriveny
                    ? 'Sign in to access the Triveny marketing & sales platform'
                    : 'Sign in or register in order to access the WeaveIn platform'}
                </CardDescription>
              </CardHeader>

              <form onSubmit={handleLogin}>
                <CardContent className="space-y-4">
                  {error && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="your.email@organization.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        disabled={loading}
                        className="pl-10"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="password">Password</Label>
                      <Link
                        to="/forgot-password"
                        className="text-sm text-v2accent-ink hover:underline"
                        style={accentTextStyle}
                      >
                        Forgot password?
                      </Link>
                    </div>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input
                        id="password"
                        type="password"
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        disabled={loading}
                        className="pl-10"
                        autoComplete="current-password"
                      />
                    </div>
                  </div>
                </CardContent>

                <CardFooter className="flex flex-col space-y-4">
                  <Button
                    type="submit"
                    className="w-full bg-v2accent hover:brightness-90"
                    style={primaryBtnStyle}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        Signing in...
                      </>
                    ) : (
                      <>
                        <LogIn className="w-4 h-4 mr-2" />
                        Sign In
                      </>
                    )}
                  </Button>

                  {/* OAuth providers — Triveny-only. Buttons hide themselves
                      automatically if the backend reports no provider is
                      configured, so this is safe to mount unconditionally. */}
                  {config.isTriveny && (
                    <div className="w-full">
                      <OAuthProviderButtons />
                    </div>
                  )}

                  <div className="text-center text-sm text-gray-600 dark:text-gray-300">
                    {config.isTriveny ? (
                      <>Need access? Contact your Triveny administrator.</>
                    ) : (
                      <>
                        Need an account?{' '}
                        <Link
                          to="/signup"
                          className="text-v2accent-ink hover:underline font-medium"
                          style={accentTextStyle}
                        >
                          Request access
                        </Link>
                      </>
                    )}
                  </div>
                </CardFooter>
              </form>
            </Card>

            {/* Certifications & Compliance — Triveny excludes HIPAA (not a covered entity) */}
            <Card className="mt-6 bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-700 dark:text-gray-200 flex items-center">
                  <Shield className="w-4 h-4 mr-2 text-green-600" />
                  Security & Compliance
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="grid grid-cols-2 gap-3">
                  {!config.isTriveny && (
                    <div className="flex items-center space-x-2 text-xs text-gray-600 dark:text-gray-300">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span>HIPAA Compliant</span>
                    </div>
                  )}
                  {/* SOC 2 Type II audit is in process, not attained: no green check. */}
                  <div className="flex items-center space-x-2 text-xs text-gray-600 dark:text-gray-300">
                    <Clock className="w-4 h-4 text-amber-500" />
                    <span>SOC 2 Type II (in process)</span>
                  </div>
                  <div className="flex items-center space-x-2 text-xs text-gray-600 dark:text-gray-300">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>256-bit Encryption</span>
                  </div>
                  <div className="flex items-center space-x-2 text-xs text-gray-600 dark:text-gray-300">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>2FA Required</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* App Downloads Section — WeaveIn-only; Triveny has no native apps yet */}
          <div className="space-y-6">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                {config.isTriveny ? 'Welcome to Triveny' : 'Get the WeaveIn App'}
              </h2>
              <p className="text-gray-600 dark:text-gray-300">
                {config.isTriveny
                  ? 'Marketing & Sales platform for B2B growth teams'
                  : 'Access WeaveIn on your preferred device for the best experience'}
              </p>
            </div>

            {/* WeaveIn-only: native-app downloads. Triveny has no apps. */}
            {!config.isTriveny && (
              <>
                {/* Desktop Apps */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center">
                      <Monitor className="w-5 h-5 mr-2 text-v2accent" />
                      Desktop Apps
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* macOS — available once the signed/notarized build is published.
                        Gated on the flag only (NOT the live manifest fetch) so an
                        ad-blocker/network hiccup fetching latest.json can't demote a
                        shipped app to "Coming Soon". The version is shown only when it
                        resolves. */}
                    {DESKTOP_MACOS_PUBLISHED ? (
                      <a
                        href={macDownloadUrl(desktopVersion)}
                        className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-v2accent hover:bg-v2accent-soft transition-colors group"
                      >
                        <div className="flex items-center space-x-3">
                          <Apple className="w-8 h-8 text-gray-700 dark:text-gray-200" />
                          <div>
                            <p className="font-medium text-gray-900 dark:text-gray-100">macOS</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Download for Mac{desktopVersion ? ` • v${desktopVersion}` : ''}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Badge variant="accent">Available</Badge>
                          <Download className="w-5 h-5 text-gray-400 group-hover:text-v2accent" />
                        </div>
                      </a>
                    ) : (
                      <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                        <div className="flex items-center space-x-3">
                          <Apple className="w-8 h-8 text-gray-700 dark:text-gray-200" />
                          <div>
                            <p className="font-medium text-gray-900 dark:text-gray-100">macOS</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Desktop app</p>
                          </div>
                        </div>
                        <Badge variant="muted">Coming Soon</Badge>
                      </div>
                    )}

                    {/* Windows — link only once a signed build is published */}
                    {DESKTOP_WINDOWS_PUBLISHED ? (
                      <a
                        href={windowsDownloadUrl(desktopVersion)}
                        className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-v2accent hover:bg-v2accent-soft transition-colors group"
                      >
                        <div className="flex items-center space-x-3">
                          <Monitor className="w-8 h-8 text-gray-700 dark:text-gray-200" />
                          <div>
                            <p className="font-medium text-gray-900 dark:text-gray-100">Windows</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Download for PC{desktopVersion ? ` • v${desktopVersion}` : ''}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Badge variant="accent">Available</Badge>
                          <Download className="w-5 h-5 text-gray-400 group-hover:text-v2accent" />
                        </div>
                      </a>
                    ) : (
                      <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                        <div className="flex items-center space-x-3">
                          <Monitor className="w-8 h-8 text-gray-700 dark:text-gray-200" />
                          <div>
                            <p className="font-medium text-gray-900 dark:text-gray-100">Windows</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Download for PC</p>
                          </div>
                        </div>
                        <Badge variant="muted">Coming Soon</Badge>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Mobile Apps */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center">
                      <Smartphone className="w-5 h-5 mr-2 text-v2accent" />
                      Mobile Apps
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {MOBILE_IOS_PUBLISHED ? (
                      <a
                        href={IOS_APP_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-v2accent hover:bg-v2accent-soft transition-colors group"
                      >
                        <div className="flex items-center space-x-3">
                          <Apple className="w-8 h-8 text-gray-700 dark:text-gray-200" />
                          <div>
                            <p className="font-medium text-gray-900 dark:text-gray-100">iOS App</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">iPhone & iPad</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Badge variant="accent">Available</Badge>
                          <Download className="w-5 h-5 text-gray-400 group-hover:text-v2accent" />
                        </div>
                      </a>
                    ) : (
                      <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                        <div className="flex items-center space-x-3">
                          <Apple className="w-8 h-8 text-gray-700 dark:text-gray-200" />
                          <div>
                            <p className="font-medium text-gray-900 dark:text-gray-100">iOS App</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">iPhone & iPad</p>
                          </div>
                        </div>
                        <Badge variant="muted">Coming Soon</Badge>
                      </div>
                    )}

                    {MOBILE_ANDROID_PUBLISHED ? (
                      <a
                        href={ANDROID_APP_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-v2accent hover:bg-v2accent-soft transition-colors group"
                      >
                        <div className="flex items-center space-x-3">
                          <Smartphone className="w-8 h-8 text-gray-700 dark:text-gray-200" />
                          <div>
                            <p className="font-medium text-gray-900 dark:text-gray-100">Android App</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Phone & Tablet</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Badge variant="accent">Available</Badge>
                          <Download className="w-5 h-5 text-gray-400 group-hover:text-v2accent" />
                        </div>
                      </a>
                    ) : (
                      <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                        <div className="flex items-center space-x-3">
                          <Smartphone className="w-8 h-8 text-gray-700 dark:text-gray-200" />
                          <div>
                            <p className="font-medium text-gray-900 dark:text-gray-100">Android App</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Phone & Tablet</p>
                          </div>
                        </div>
                        <Badge variant="muted">Coming Soon</Badge>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}

            {/* Triveny-only: highlights instead of native-app downloads. */}
            {config.isTriveny && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-semibold text-gray-900 dark:text-gray-100">
                    What's in your workspace
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul style={{ display: 'flex', flexDirection: 'column', gap: 10, margin: 0, padding: 0, listStyle: 'none' }}>
                    {[
                      { eyebrow: 'DESIGN', label: 'Landing pages, forms, assets' },
                      { eyebrow: 'MARKET', label: 'Campaigns, audiences, journeys, analytics' },
                      { eyebrow: 'ENGAGE', label: 'Pipeline, inbox, calls, intelligence' },
                      { eyebrow: 'OPERATE', label: 'RevOps + Support' },
                    ].map((item) => (
                      <li
                        key={item.eyebrow}
                        style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}
                      >
                        <span
                          style={{
                            fontFamily: tvTokens.fontMono,
                            fontSize: 10,
                            fontWeight: 600,
                            letterSpacing: '0.14em',
                            color: tvTokens.ink500,
                            minWidth: 64,
                          }}
                        >
                          {item.eyebrow}
                        </span>
                        <span style={{ fontSize: 13.5, color: tvTokens.ink700 }}>{item.label}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Web App - Current Platform */}
            <Card
              className="bg-surface border-v2border"
              style={config.isTriveny ? { background: tvTokens.surface, borderColor: tvTokens.border, backgroundImage: 'none' } : undefined}
            >
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div
                      className="w-10 h-10 rounded-full bg-v2accent flex items-center justify-center"
                      style={accentBgStyle}
                    >
                      <CheckCircle className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-gray-100">
                        {config.isTriveny ? 'Triveny web app' : 'Web App'}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">You're currently using the web app</p>
                    </div>
                  </div>
                  <Badge variant="accent">Active</Badge>
                </div>
              </CardContent>
            </Card>

            {/* Help Section */}
            <div className="text-center text-sm text-gray-500 dark:text-gray-400 pt-4">
              {config.isTriveny ? (
                <p>
                  Need help? Contact{' '}
                  <a href="mailto:support@triveny.ai" className="text-v2accent-ink hover:underline" style={accentTextStyle}>
                    support@triveny.ai
                  </a>
                </p>
              ) : (
                <p>
                  Need help? Contact{' '}
                  <a href="mailto:support@weavein.ai" className="text-v2accent-ink hover:underline">
                    support@weavein.ai
                  </a>
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer — HIPAA link is healthcare-only; Triveny excludes it */}
      <footer className="bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 mt-8 sm:mt-12">
        <div className="max-w-6xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-3 md:space-y-0">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              © {new Date().getFullYear()} {config.isTriveny ? 'Triveny.ai by Trust' : 'WeaveIn.ai by Trust'}. All rights reserved.
            </p>
            <div className="flex items-center space-x-6 text-sm">
              <Link to="/consent/terms-of-use" className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">Terms of Use</Link>
              {!config.isTriveny && (
                <Link to="/consent/hipaa-authorization" className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">HIPAA</Link>
              )}
              <Link to="/consent/privacy-policy" className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">Privacy Policy</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
